import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { MapPin, Camera, Video, Check, ArrowLeft, LocateFixed, Search, Loader2, LogOut } from "lucide-react";

const SLOTS = [
  { key: "exterior", label: "Exterior", required: true, icon: Camera },
  { key: "interior", label: "Interior", required: true, icon: Camera },
  { key: "experience", label: "Experience", required: true, icon: Camera },
  { key: "food", label: "Food & Drink", icon: Camera },
  { key: "menu", label: "Menu", icon: Camera },
  { key: "event", label: "Event", icon: Camera },
  { key: "team", label: "Team", icon: Camera },
  { key: "video", label: "Video", icon: Video },
];
const REQUIRED = ["exterior", "interior", "experience"];

function haversine(a, b, c, d) {
  const R = 6371, r = Math.PI / 180;
  const dLat = (c - a) * r, dLng = (d - b) * r;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(a * r) * Math.cos(c * r) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

function LoginGate() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(email, password); } catch { toast.error("Login failed"); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4" data-testid="team-login">
        <div className="text-center mb-6">
          <div className="font-display font-black text-3xl text-white">RiNo <span className="text-[#FF0055]">Field Kit</span></div>
          <p className="text-neutral-500 text-sm mt-1">Team photo uploads, on location.</p>
        </div>
        <input data-testid="team-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-[#141414] border border-white/10 px-4 py-3 text-white text-base focus:outline-none focus:border-[#FF0055]" />
        <input data-testid="team-password" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-[#141414] border border-white/10 px-4 py-3 text-white text-base focus:outline-none focus:border-[#FF0055]" />
        <button disabled={busy} data-testid="team-login-btn" className="w-full bg-[#FF0055] text-white font-semibold py-3 disabled:opacity-50">{busy ? "Signing in…" : "Sign In"}</button>
      </form>
    </div>
  );
}

export default function TeamUpload() {
  const { user, loading, logout } = useAuth();
  const [venues, setVenues] = useState([]);
  const [coords, setCoords] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState("");
  const fileRef = useRef();
  const slotRef = useRef("exterior");

  useEffect(() => {
    if (user) api.get("/businesses").then((r) => setVenues(r.data));
  }, [user]);

  const loadMedia = (slug) => api.get("/admin/media", { params: { venue_slug: slug } }).then((r) => setMedia(r.data));
  const selectVenue = (v) => { setSelected(v); setMedia([]); loadMedia(v.slug); };

  const useLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not available");
    toast.info("Finding venues near you…");
    navigator.geolocation.getCurrentPosition(
      (p) => { setCoords({ lat: p.coords.latitude, lng: p.coords.longitude }); toast.success("Sorted by distance"); },
      () => toast.error("Couldn't get your location"),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const pickSlot = (key) => {
    slotRef.current = key;
    if (fileRef.current) {
      fileRef.current.accept = key === "video" ? "video/*" : "image/*";
      fileRef.current.setAttribute("capture", key === "video" ? "environment" : "environment");
      fileRef.current.click();
    }
  };

  const onFile = async (e) => {
    const file = e.target.files[0];
    if (!file || !selected) return;
    const slot = slotRef.current;
    const wantVideo = slot === "video";
    const okType = wantVideo ? file.type.startsWith("video/") : file.type.startsWith("image/");
    if (!okType) { toast.error(`Please pick a ${wantVideo ? "video" : "photo"} file`); e.target.value = ""; return; }
    const maxMB = wantVideo ? 300 : 40;
    if (file.size > maxMB * 1_000_000) { toast.error(`File too large (max ${maxMB}MB)`); e.target.value = ""; return; }
    setUploading(slot);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("venue_slug", selected.slug);
    fd.append("slot", slot);
    fd.append("source", "team");
    try {
      await api.post("/admin/media/upload", fd);
      toast.success(`${slot} photo uploaded!`);
      loadMedia(selected.slug);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(""); if (fileRef.current) fileRef.current.value = ""; }
  };

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-neutral-500">Loading…</div>;
  if (!user) return <LoginGate />;

  const have = new Set(media.filter((m) => m.media_type === "photo").map((m) => m.slot));
  let list = venues.filter((v) => v.name.toLowerCase().includes(q.toLowerCase()));
  if (coords) {
    list = list.filter((v) => v.lat && v.lng)
      .map((v) => ({ ...v, _d: haversine(coords.lat, coords.lng, v.lat, v.lng) }))
      .sort((a, b) => a._d - b._d);
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-24" data-testid="team-upload">
      <header className="sticky top-0 z-10 bg-[#0d0d0d] border-b border-white/10 px-4 h-14 flex items-center justify-between">
        <div className="font-display font-black text-lg">RiNo <span className="text-[#FF0055]">Field Kit</span></div>
        <div className="flex items-center gap-3">
          <Link to="/admin" className="text-neutral-400 text-xs">Admin</Link>
          <button onClick={logout} data-testid="team-logout" className="text-neutral-400 hover:text-[#FF0055]"><LogOut size={18} /></button>
        </div>
      </header>

      <input ref={fileRef} type="file" onChange={onFile} className="hidden" data-testid="team-file-input" />

      {!selected ? (
        <div className="px-4 py-4">
          <button onClick={useLocation} data-testid="team-use-location" className="w-full flex items-center justify-center gap-2 bg-[#00E5FF] text-black font-bold py-3 mb-3">
            <LocateFixed size={18} /> {coords ? "Near me ✓ — re-sort" : "Find venues near me"}
          </button>
          <div className="relative mb-4">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search venues…" data-testid="team-search" className="w-full bg-[#141414] border border-white/10 pl-9 pr-3 py-3 text-base focus:outline-none focus:border-[#FF0055]" />
          </div>
          <div className="space-y-2">
            {list.slice(0, 60).map((v) => (
              <button key={v.slug} onClick={() => selectVenue(v)} data-testid={`team-venue-${v.slug}`}
                className="w-full text-left bg-[#141414] border border-white/10 active:border-[#FF0055] px-4 py-3 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-white text-base truncate">{v.name}</div>
                  <div className="text-neutral-500 text-xs truncate">{v.category}</div>
                </div>
                {coords && v._d != null && <span className="text-[#00E5FF] text-xs flex-shrink-0 ml-2">{v._d < 1 ? `${Math.round(v._d * 1000)} m` : `${v._d.toFixed(1)} km`}</span>}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="px-4 py-4">
          <button onClick={() => setSelected(null)} data-testid="team-back" className="flex items-center gap-2 text-neutral-400 text-sm mb-3"><ArrowLeft size={16} /> All venues</button>
          <h1 className="font-display font-black text-2xl">{selected.name}</h1>
          {selected.address && <p className="text-neutral-500 text-sm flex items-center gap-1.5 mt-1"><MapPin size={13} /> {selected.address}</p>}

          <div className="flex gap-2 mt-4" data-testid="team-required-status">
            {REQUIRED.map((s) => (
              <div key={s} className={`flex-1 text-center py-2 border text-xs font-medium ${have.has(s) ? "border-[#00FF66]/40 bg-[#00FF66]/10 text-[#00FF66]" : "border-[#FF0055]/40 bg-[#FF0055]/10 text-[#FF0055]"}`}>
                {have.has(s) ? <Check size={13} className="inline mr-1" /> : null}{s}
              </div>
            ))}
          </div>
          <p className="text-neutral-500 text-xs mt-2">Tap a shot type to open your camera. Your photo's location is saved automatically.</p>

          <div className="grid grid-cols-2 gap-3 mt-4">
            {SLOTS.map((s) => {
              const count = media.filter((m) => m.slot === s.key).length;
              const busy = uploading === s.key;
              return (
                <button key={s.key} onClick={() => pickSlot(s.key)} disabled={busy} data-testid={`team-slot-${s.key}`}
                  className={`relative flex flex-col items-center justify-center gap-2 py-6 border active:scale-[0.98] transition-transform ${s.required && count === 0 ? "border-[#FF0055]/50 bg-[#FF0055]/5" : "border-white/10 bg-[#141414]"}`}>
                  {busy ? <Loader2 size={22} className="animate-spin text-[#00E5FF]" /> : <s.icon size={22} className="text-[#FF0055]" />}
                  <span className="text-sm font-semibold">{s.label}</span>
                  {s.required && <span className="text-[9px] text-neutral-500">required</span>}
                  {count > 0 && <span className="absolute top-2 right-2 bg-[#00FF66] text-black text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">{count}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
